/**
 * Plugin for the `video` module kind (F3-F) for the CourseModuleRegistry (F2-A).
 *
 * A `video` module references a `video_asset` (via `config.videoAssetId`) and
 * plays it as a path stop (same player as the `video` block). It gets
 * added by REGISTERING this plugin — without touching the authoring/player core
 * (the Moodle spine is extensible). In v1 it doesn't contribute to the course's
 * completion/grade (parallel to `resource`): watching is tracked in `video_watch_progress`.
 */

import type { FeatureManifest } from "../academy/country/packs";
import type { ModuleComponentLoaders } from "../academy/modules/plugins";
import {
  type CourseModulePlugin,
  isPlainObject,
  type ModuleValidationResult,
  type SchemaValidator,
} from "../academy/modules/types";

/** Config for the `video` module (mirrors the reference to the `video_asset`). */
export interface VideoModuleConfig extends Record<string, unknown> {
  /** Id of the `video_asset` to play (empty = not yet assigned). */
  videoAssetId?: string;
}

const VIDEO_MODULE_MANIFEST: FeatureManifest = {
  key: "academy.module.video",
  name: "Vídeo",
  description:
    "Vídeo bajo demanda (VOD) como parada de la senda: reproducción con reanudación y traza de visionado.",
  scope: "course",
  status: "stable",
};

/** Validates the `video` module's config: `videoAssetId` optional (string). */
export function validateVideoModuleConfig(
  value: unknown,
): ModuleValidationResult<VideoModuleConfig> {
  if (value === null || value === undefined) {
    return { ok: true, value: {} };
  }
  if (!isPlainObject(value)) {
    return { ok: false, errors: ["video module config must be an object"] };
  }
  if (value.videoAssetId !== undefined && typeof value.videoAssetId !== "string") {
    return { ok: false, errors: ["video module `videoAssetId` must be a string"] };
  }
  const cfg: VideoModuleConfig = {};
  if (typeof value.videoAssetId === "string" && value.videoAssetId.trim() !== "") {
    cfg.videoAssetId = value.videoAssetId;
  }
  return { ok: true, value: cfg };
}

const videoModuleConfigSchema: SchemaValidator<VideoModuleConfig> = {
  parse: validateVideoModuleConfig,
};

export function createVideoModulePlugin<TComponent = unknown>(
  components: ModuleComponentLoaders<TComponent> = {},
  // `VideoModuleConfig` is documentational: structurally it's equivalent to the
  // default (Record<string,unknown>) but it names the module config's contract.
  // oxlint-disable-next-line typescript/no-unnecessary-type-arguments
): CourseModulePlugin<TComponent, VideoModuleConfig> {
  return {
    kind: "video",
    manifest: VIDEO_MODULE_MANIFEST,
    configSchema: videoModuleConfigSchema,
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    // v1: doesn't gate the course's completion/grade (watching is tracked separately).
    completionContribution: () => null,
    gradeContribution: () => null,
  };
}
